import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import moment from "moment";
import DatePicker from "../date/DatePicker";
import FormField from "./FormField";

// #region CONSTANTS
const DEFAULT_FORMAT = "DD/MM/YYYY";
const DEFAULT_CALENDAR_ICON = <span className="fa-light fa-calendar" />;
// #endregion

/** FormFieldDatePicker component - combines FormField with DatePicker
 * @param {moment} value - Controlled value as a moment object
 * @param {function} onChange - Callback function called when the date changes (receives date and dateString)
 * @param {string} displayValue - Optional manual override for the displayed input value
 * @param {string} format - Date format (default: 'DD/MM/YYYY')
 * @param {string|moment|Date} disableBefore - Disable dates before this date (accepts presets like "today", moment instance, Date, etc.)
 * @param {string|moment|Date} disableAfter - Disable dates after this date (accepts presets like "today", moment instance, Date, etc.)
 * @param {function} disabledDate - Custom disabledDate function (receives current moment)
 * @param {string} picker - Ant Design DatePicker picker mode (e.g., 'date', 'week', 'month', 'quarter', 'year')
 * @param {boolean} disabled - Whether the form field is disabled
 * @param {boolean} allowClear - Whether to show a clear button inside the picker popup or input
 * @param {string} popupClassName - Class name for the datepicker popup container
 * @param {object} popupStyle - Inline styling for the datepicker popup container
 * @param {object} pickerProps - Additional raw props passed to the underlying Ant Design DatePicker
 * @param {string} className - Class name for the outer wrapper element
 * @param {object} style - Inline style for the outer wrapper element
 * @param {string|number} width - Custom CSS width for the outer wrapper element
 * @param {React.ReactNode} leftContent - Custom React node to render on the left side of the input (default: calendar icon)
 * @param {boolean} [useRightClearBtn=true] - Whether to show the right clear X button automatically
 * @param {function} [onClearAction] - Optional custom callback when the clear X button is clicked
 * @param {function} [onFormCardChanges] - Callback indicating value changed (typically sets form card dirty status)
 */
const FormFieldDatePicker = forwardRef(
    (
        {
            value,
            onChange,
            displayValue,
            format = DEFAULT_FORMAT,
            disableBefore,
            disableAfter,
            disabledDate,
            defaultPickerValue,
            picker,
            disabled = false,
            allowClear,
            popupClassName,
            popupStyle,
            pickerProps,
            className,
            style,
            width,
            leftContent = DEFAULT_CALENDAR_ICON,
            useRightClearBtn = true,
            onClearAction,
            onFormCardChanges,
            ...formFieldProps
        },
        ref
    ) => {
        // #region REFS
        const formFieldRef = useRef(null);
        // #endregion

        // #region METHODS
        const handleChange = (date, dateString) => {
            onChange?.(date, dateString);
            onFormCardChanges?.(true);
        };

        const handleClearClick = e => {
            e.stopPropagation();
            if (disabled) return;
            if (onClearAction) {
                onClearAction();
            } else {
                handleChange(null, "");
            }
        };

        const isValid = highlight => formFieldRef.current?.isValid?.(highlight) ?? true;

        const getValue = () => value ?? null;

        const setOutsideInvalid = (...args) => formFieldRef.current?.setOutsideInvalid?.(...args);
        // #endregion

        // #region COMPUTED PROPS
        const formattedDisplayValue = useMemo(() => {
            if (displayValue !== undefined) return displayValue;
            if (!value) return "";

            const formattedDate = moment(value).format(format);
            return formattedDate === "Invalid date" ? "" : formattedDate;
        }, [displayValue, format, value]);

        const defaultClearBtn = (
            <span
                className={`fa-light fa-xmark w-100 h-100 ${disabled ? "text-inactive" : "text-normal"}`}
                style={{ alignContent: "center", justifyContent: "center", textAlign: "center" }}
                onClick={handleClearClick}
            />
        );

        const effectiveRightContent =
            formFieldProps.rightContent !== undefined
                ? formFieldProps.rightContent
                : useRightClearBtn
                  ? defaultClearBtn
                  : undefined;
        // #endregion

        useImperativeHandle(ref, () => ({
            isValid,
            getValue,
            setOutsideInvalid
        }));

        // #region RENDER
        return (
            <DatePicker
                value={value}
                onChange={handleChange}
                format={format}
                disableBefore={disableBefore}
                disableAfter={disableAfter}
                disabledDate={disabledDate}
                defaultPickerValue={defaultPickerValue}
                picker={picker}
                disabled={disabled}
                allowClear={allowClear}
                popupClassName={popupClassName}
                popupStyle={popupStyle}
                pickerProps={pickerProps}
                className={className}
                style={style}
                width={width}>
                <FormField
                    {...formFieldProps}
                    ref={formFieldRef}
                    type="text"
                    leftContent={leftContent}
                    rightContent={effectiveRightContent}
                    value={formattedDisplayValue}
                />
            </DatePicker>
        );
        // #endregion
    }
);

export default FormFieldDatePicker;
